<script setup lang="ts" generic="T extends object">
import { useI18n } from "vue-i18n";
import VueSelect from "vue-select";

import { ChevronIcon, LoaderIcon } from "./icons";

const model = defineModel<T | null>({ required: true });

const {
    options,
    label,
    isLoading = false,
    disabled = false,
} = defineProps<{
    options: T[];
    /** Поле опции, которое показывается в списке */
    label: keyof T & string;
    isLoading?: boolean;
    disabled?: boolean;
}>();

const { t } = useI18n({ useScope: "global" });
</script>

<template>
    <VueSelect v-model="model" :options="options" :label="label" :disabled="disabled">
        <template #open-indicator="{ attributes }">
            <LoaderIcon v-if="isLoading" />
            <ChevronIcon v-else v-bind="attributes" />
        </template>
        <template #no-options>
            {{ t("select.notFound") }}
        </template>
    </VueSelect>
</template>

<!-- Not scoped: vue-select renders its own markup. The styles stay inside the shadow root anyway -->
<style>
/* Based on vue-select styles, adapted to the widget design (from v2) */
.v-select {
    --vs-colors--lightest: var(--custom-color-gray);
    --vs-colors--light: rgb(60 60 60 / 50%);
    --vs-colors--dark: #333;
    --vs-colors--darkest: rgb(0 0 0 / 15%);
    --vs-search-input-color: inherit;
    --vs-search-input-placeholder-color: inherit;
    --vs-font-size: 1rem;
    --vs-line-height: 1.4;
    --vs-state-disabled-bg: rgb(248 248 248);
    --vs-state-disabled-color: var(--vs-colors--light);
    --vs-state-disabled-cursor: not-allowed;
    --vs-border-color: var(--vs-colors--lightest);
    --vs-border-width: 1px;
    --vs-border-style: solid;
    --vs-border-radius: 6px;
    --vs-actions-padding: 2px 6px 0 3px;
    --vs-controls-color: var(--vs-colors--light);
    --vs-controls-size: 1;
    --vs-selected-bg: #f0f0f0;
    --vs-selected-color: var(--vs-colors--dark);
    --vs-selected-border-color: var(--vs-border-color);
    --vs-selected-border-style: var(--vs-border-style);
    --vs-selected-border-width: var(--vs-border-width);
    --vs-dropdown-bg: #fff;
    --vs-dropdown-color: inherit;
    --vs-dropdown-z-index: 1000;
    --vs-dropdown-min-width: 160px;
    --vs-dropdown-max-height: 146px;
    --vs-dropdown-box-shadow: 0 3px 6px 0 var(--vs-colors--darkest);
    --vs-dropdown-option-color: var(--vs-dropdown-color);
    --vs-dropdown-option-padding: 10px 16px;
    --vs-dropdown-option--active-bg: #f4f6f8;
    --vs-dropdown-option--active-color: #000;
    --vs-transition-timing-function: cubic-bezier(1, 0.5, 0.8, 1);
    --vs-transition-duration: 0.15s;

    position: relative;
    font-family: inherit;
    transition: box-shadow 150ms ease;

    &,
    & * {
        box-sizing: border-box;
    }

    @media screen and (min-width: 768px) {
        --vs-dropdown-max-height: 350px;
    }
}

.vs__fade-enter-active,
.vs__fade-leave-active {
    pointer-events: none;
    transition: opacity var(--vs-transition-duration) var(--vs-transition-timing-function);
}

.vs__fade-enter-from,
.vs__fade-leave-to {
    opacity: 0;
}

.vs--disabled {
    .vs__dropdown-toggle,
    .vs__clear,
    .vs__search,
    .vs__selected,
    .vs__open-indicator {
        cursor: var(--vs-state-disabled-cursor);
        background-color: var(--vs-state-disabled-bg);
    }
}

.vs__dropdown-toggle {
    display: flex;
    padding: 5px;
    appearance: none;
    background: none;
    border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
    border-radius: var(--vs-border-radius);
    white-space: normal;
    transition: border-color 150ms ease;

    &:hover {
        border-color: var(--custom-color-main-hover, var(--custom-color-main));
    }
}

.vs__selected-options {
    position: relative;
    display: flex;
    flex-basis: 100%;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0 2px;
}

.vs__actions {
    display: flex;
    align-items: center;
    padding: var(--vs-actions-padding);
}

.vs--searchable .vs__dropdown-toggle {
    cursor: text;
}

.vs--unsearchable .vs__dropdown-toggle {
    cursor: pointer;
}

.vs--open {
    box-shadow: 0 1px 4px 0 rgb(105 89 203 / 40%);

    .vs__dropdown-toggle {
        border-color: var(--custom-color-main);
        border-bottom-color: transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    .vs__open-indicator {
        transform: rotate(180deg) scale(var(--vs-controls-size));
    }
}

.vs__open-indicator {
    transform: scale(var(--vs-controls-size));
    transition: transform var(--vs-transition-duration) var(--vs-transition-timing-function);
}

.vs__clear {
    margin-right: 8px;
    padding: 0;
    fill: var(--vs-controls-color);
    background-color: transparent;
    border: 0;
    cursor: pointer;
}

.vs__dropdown-menu {
    position: absolute;
    top: calc(100% - var(--vs-border-width));
    left: 0;
    z-index: var(--vs-dropdown-z-index);
    display: block;
    width: 100%;
    min-width: var(--vs-dropdown-min-width);
    max-height: var(--vs-dropdown-max-height);
    margin: 0;
    padding: 5px 0;
    overflow-y: auto;
    list-style: none;
    text-align: left;
    color: var(--vs-dropdown-color);
    background: var(--vs-dropdown-bg);
    border: var(--vs-border-width) var(--vs-border-style) var(--custom-color-main);
    border-radius: 0 0 var(--vs-border-radius) var(--vs-border-radius);
    box-shadow: var(--vs-dropdown-box-shadow);
}

.vs__no-options {
    text-align: center;
}

.vs__dropdown-option {
    display: block;
    clear: both;
    padding: var(--vs-dropdown-option-padding);
    line-height: 1.42857143;
    color: var(--vs-dropdown-option-color);
    white-space: nowrap;
    cursor: pointer;

    @media screen and (max-width: 767px) {
        padding: 8px;
        font-size: var(--custom-font-size);
    }
}

.vs__dropdown-option--highlight {
    color: var(--vs-dropdown-option--active-color);
    background: var(--vs-dropdown-option--active-bg);
}

.vs__dropdown-option--disabled {
    color: var(--vs-state-disabled-color);
    background: var(--vs-state-disabled-bg);
    cursor: var(--vs-state-disabled-cursor);
}

.vs__selected {
    z-index: 0;
    display: flex;
    align-items: center;
    margin: 0 2px;
    padding: 0 0.25em;
    line-height: var(--vs-line-height);
    color: var(--vs-selected-color);
    background-color: var(--vs-selected-bg);
    border: var(--vs-selected-border-width) var(--vs-selected-border-style)
        var(--vs-selected-border-color);
    border-radius: var(--vs-border-radius);
    transition: opacity 150ms ease;

    @media screen and (max-width: 767px) {
        font-size: var(--custom-font-size);
    }
}

/* A long value wraps its own text instead of pushing the search input to a new line */
.vs--single {
    .vs__selected-options {
        flex-wrap: nowrap;
        min-width: 0;
    }

    .vs__selected {
        min-width: 0;
        overflow-wrap: anywhere;
        background-color: transparent;
        border-color: transparent;
    }

    &.vs--open .vs__selected,
    &.vs--loading .vs__selected {
        position: absolute;
        margin-top: 2px;
        opacity: 0.4;

        @media screen and (max-width: 767px) {
            margin-top: 3px;
        }
    }

    &.vs--searching .vs__selected {
        display: none;
    }

    &.vs--searching:not(.vs--open):not(.vs--loading) .vs__search {
        opacity: 0.2;
    }
}

.vs__search::-webkit-search-cancel-button,
.vs__search::-webkit-search-decoration,
.vs__search::-webkit-search-results-button,
.vs__search::-webkit-search-results-decoration {
    display: none;
}

.vs__search,
.vs__search:focus {
    z-index: 1;
    flex-grow: 1;
    width: 0;
    max-width: 100%;
    margin: 2px 0;
    padding: 0 7px;
    font-size: var(--vs-font-size);
    line-height: var(--vs-line-height);
    color: var(--vs-search-input-color);
    appearance: none;
    background: none;
    border: 1px solid transparent;
    border-left: none;
    outline: none;
    box-shadow: none;
}

.vs__search::placeholder {
    color: var(--vs-search-input-placeholder-color);
}

.vs--unsearchable {
    .vs__search {
        opacity: 1;
    }

    &:not(.vs--disabled) .vs__search {
        cursor: pointer;
    }
}
</style>
